import { useEffect } from 'react';

export const BODY_SCROLL_LOCK_CLASS = 'pdp-scroll_locked';
export const SHELL_SCROLL_LOCK_EVENT = 'ESPP3.PDP.BodyScrollLock';
const SHELL_SCROLL_LOCK_EVENT_SIGNATURE = 'ESPP3.PDP.BodyScrollLockHook';

export type ShellScrollLockDetail = {
    source: string;
    locked: boolean;
};

type ShellScrollLockEvent = CustomEvent<ShellScrollLockDetail>;

const activeLockSources = new Set<string>();
const activeShellLockSources = new Set<string>();

let isBodyLocked = false;
let classWasAlreadyPresent = false;

function isValidSource(source: unknown): source is string {
    return typeof source === 'string' && source.trim().length > 0;
}

function syncBodyScrollLock(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
    }

    const shouldLock = activeLockSources.size > 0;

    if (shouldLock === isBodyLocked) {
        return;
    }

    const body = document.body;

    if (shouldLock) {
        classWasAlreadyPresent = body.classList.contains(
            BODY_SCROLL_LOCK_CLASS
        );

        body.classList.add(BODY_SCROLL_LOCK_CLASS);

        isBodyLocked = true;
        return;
    }

    if (!classWasAlreadyPresent) {
        body.classList.remove(BODY_SCROLL_LOCK_CLASS);

        if (body.classList.length === 0) {
            body.removeAttribute('class');
        }
    }

    isBodyLocked = false;
}

export function lockBodyScroll(source: string): void {
    if (!isValidSource(source)) {
        return;
    }

    activeLockSources.add(source);
    syncBodyScrollLock();
}

export function unlockBodyScroll(source: string): void {
    if (!isValidSource(source)) {
        return;
    }

    activeLockSources.delete(source);
    syncBodyScrollLock();
}

export function useShellBodyScrollLock(): void {
    useEffect(() => {
        const handleShellScrollLock = (event: Event): void => {
            const { detail } = event as ShellScrollLockEvent;
            const { source, locked } = detail ?? {};

            if (!isValidSource(source) || typeof locked !== 'boolean') {
                return;
            }

            if (locked) {
                activeShellLockSources.add(source);
                lockBodyScroll(source);
            } else {
                activeShellLockSources.delete(source);
                unlockBodyScroll(source);
            }
        };

        window.shell.subscribeTo(
            SHELL_SCROLL_LOCK_EVENT,
            handleShellScrollLock,
            SHELL_SCROLL_LOCK_EVENT_SIGNATURE
        );

        return () => {
            window.shell.unsubscribeFrom(SHELL_SCROLL_LOCK_EVENT, SHELL_SCROLL_LOCK_EVENT_SIGNATURE);

            for (const source of activeShellLockSources) {
                unlockBodyScroll(source);
            }

            activeShellLockSources.clear();
        };
    }, []);
}
